import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ShiftHandling } from '../api/types';
import useShiftHandling from '../hooks/useShiftHandling';

import ManageJobSurge from '@/routes/Agency/PaySurge/components/ManageJobSurge';

type Props = {
  agencyId: number;
  shiftId: number;
  jobId: number;
  startAt?: string | null;
  handling: ShiftHandling;
  /** Where "Manage workers" goes — the job's own staffing page. */
  jobHref: string;
  /** Re-read the panel when a claim lapses while it is open. */
  onLapse: () => void;
};

/**
 * Take Over, the top of the Shift Detail footer (GRAVY-10443). One of three
 * states, each drawn in Figma as the same panel with its footer swapped:
 *
 *   unclaimed + claimable  "Take over"                           38623:6796
 *   mine                   "Mark as resolved" + ⋮ actions menu   38627:8482
 *   other                  "Peter B. is handling this shift."    38627:10330
 *
 * An unclaimed shift that does not need attention renders nothing: the footer
 * is then exactly what it was before Take Over existed.
 *
 * The server decides every state, including expiry — this component never
 * infers that a claim has lapsed. It only asks again at `expires_at`, so a
 * panel left open does not keep showing a claim the server has let go.
 */
const ShiftHandlingControl = ({
  agencyId,
  shiftId,
  jobId,
  startAt,
  handling,
  jobHref,
  onLapse,
}: Props) => {
  const { takeOver, resolve, pending, error } = useShiftHandling(
    agencyId,
    shiftId,
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [managingSurge, setManagingSurge] = useState(false);
  const menuWrapRef = useRef<HTMLDivElement>(null);

  const { expires_at: expiresAt } = handling;
  useEffect(() => {
    if (handling.state === 'unclaimed' || !expiresAt) return undefined;
    // A second of slack so the server's clock has passed the deadline too.
    const delay = new Date(expiresAt).getTime() - Date.now() + 1_000;
    const timer = window.setTimeout(onLapse, Math.max(delay, 0));
    return () => window.clearTimeout(timer);
  }, [handling.state, expiresAt, onLapse]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      if (!menuWrapRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [menuOpen]);

  let control: JSX.Element | null = null;

  if (handling.state === 'other') {
    control = (
      <div className="dashv2-panel-handling-note" data-testid="handlingHeld">
        {handling.holder_name ?? 'Another admin'} is handling this shift.
      </div>
    );
  } else if (handling.state === 'mine') {
    control = (
      <div className="dashv2-panel-handling-row">
        <button
          type="button"
          className="dashv2-panel-btn is-filled"
          data-testid="handlingResolve"
          disabled={pending != null}
          onClick={() => resolve()}
        >
          {pending === 'resolve' ? 'Resolving…' : 'Mark as resolved'}
        </button>
        {/* The claimed-by-you actions menu (AC 10; Figma 38726:44538 / 44540). */}
        <div className="dashv2-invitedmenu-wrap" ref={menuWrapRef}>
          <button
            type="button"
            className="dashv2-panel-handling-more"
            aria-label="More shift actions"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            data-testid="handlingMenu"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <EllipsisVerticalIcon />
          </button>
          {menuOpen && (
            <div className="dashv2-invitedmenu" role="menu" data-placement="up">
              <button
                type="button"
                role="menuitem"
                className="dashv2-invitedmenu-item"
                onClick={() => {
                  setMenuOpen(false);
                  setManagingSurge(true);
                }}
              >
                Manage pay surge
              </button>
              <Link
                role="menuitem"
                className="dashv2-invitedmenu-item"
                to={jobHref}
              >
                Manage workers
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  } else if (handling.claimable) {
    control = (
      <button
        type="button"
        className="dashv2-panel-btn is-filled"
        data-testid="handlingTakeOver"
        disabled={pending != null}
        onClick={() => takeOver(startAt)}
      >
        {pending === 'take_over' ? 'Taking over…' : 'Take over'}
      </button>
    );
  }

  if (!control) return null;

  return (
    <>
      {control}
      {managingSurge && (
        <ManageJobSurge jobId={jobId} onClose={() => setManagingSurge(false)} />
      )}
      {error && (
        <p className="dashv2-panel-handling-error" role="alert">
          {error}
        </p>
      )}
    </>
  );
};

export default ShiftHandlingControl;
